import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEstateFolderShareLinks,
  getProjectFolderShareLinks,
  createEstateFolderShare,
  createProjectFolderShare,
  revokeEstateFolderShareLink,
  revokeProjectFolderShareLink,
} from '@/core/api/services/archives';

export function useFolderShareLinks(
  resourceType: 'estate' | 'project',
  id: string,
  folderId: string
) {
  const isEstate = resourceType === 'estate';
  const queryClient = useQueryClient();
  const queryKey = ['folder-share-links', resourceType, id, folderId] as const;

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () =>
      isEstate ? getEstateFolderShareLinks(id, folderId) : getProjectFolderShareLinks(id, folderId),
  });

  const shareEstate = useMutation({
    mutationFn: (vars: { folderId: string; expiresIn?: number; parts?: string[] }) =>
      createEstateFolderShare({ estateId: id, ...vars }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const shareProject = useMutation({
    mutationFn: (vars: { folderId: string; expiresIn?: number; parts?: string[] }) =>
      createProjectFolderShare({ projectId: id, ...vars }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const revokeEstate = useMutation({
    mutationFn: (vars: { folderId: string; token: string }) =>
      revokeEstateFolderShareLink(id, vars.folderId, vars.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const revokeProject = useMutation({
    mutationFn: (vars: { folderId: string; token: string }) =>
      revokeProjectFolderShareLink(id, vars.folderId, vars.token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data,
    isLoading,
    refetch,

    share: isEstate ? shareEstate.mutateAsync : shareProject.mutateAsync,
    revoke: isEstate ? revokeEstate.mutateAsync : revokeProject.mutateAsync,
  };
}
